// Whether a routing page refreshes by itself. A page someone is on asks `GET /api/routing/version`
// every 15 s and reads its data again when a marker moved. A hidden tab asks nothing and reads again
// when shown. A visible tab nobody has used for 30 minutes stops too (React Query's focus set to false:
// every `refetchInterval` waits, as in a hidden tab), says so (`IdleNotice`), and refreshes at the first
// pointer, key, scroll or touch (the focus back: `refetchOnWindowFocus`). Pure, so the checks run it.

/** A page someone is on: every 15 s. */
export const VERSION_POLL_MS = 15_000;
/** No pointer, key, wheel, scroll or touch in the tab for this long: idle, nothing refreshes. */
export const USER_IDLE_MS = 30 * 60_000;

/** The poll's interval: none in a hidden or idle tab, else 15 s. */
export const versionPollMs = (hidden: boolean, idle: boolean): false | number =>
  hidden || idle ? false : VERSION_POLL_MS;

/** "paused" for a hidden tab and an idle one, "live" otherwise. */
export const liveState = (hidden: boolean, idle: boolean): "live" | "paused" => (hidden || idle ? "paused" : "live");

/**
 * React Query's focus for an idle page: false (every interval waits; nothing refetches), else the
 * tab's own visibility (`undefined`). Going from false back to it fires `refetchOnWindowFocus` once per
 * stale query: the refresh on return.
 */
export const idleFocus = (idle: boolean): false | undefined => (idle ? false : undefined);

/** What a newer marker does to the pages' data: refetch what is on screen, or — paused — only mark it stale. */
export const sourceRefetchType = (paused: boolean): "active" | "none" => (paused ? "none" : "active");

/** The notice shows only for an idle, visible tab: a hidden one refreshes as soon as it is shown. */
export const showsIdleNotice = (idle: boolean, hidden: boolean) => idle && !hidden;

/**
 * Whether the input that ends idle is kept from the page: a press on the notice's layer or the header's
 * pill (and the click it makes) only resumes; a key only resumes unless it is typed in a dialog, which
 * stays usable over the layer; a move, wheel or scroll resumes and goes on.
 */
export const swallowsWake = (kind: "press" | "key" | "move", onNotice: boolean, inDialog: boolean) =>
  kind === "press" ? onNotice : kind === "key" ? !inDialog : false;

/**
 * Test only: `localStorage["routing.idleMs"]` (milliseconds, 10 s to 30 minutes), read once when the page
 * loads, shortens idle on staging and locally — never on production. The guard is the server's
 * `APP_ENV` (`/api/version`'s `appEnv`), not the build: staging runs the same production build.
 * Unknown yet, or anything else: 30 minutes.
 */
export const IDLE_MS_KEY = "routing.idleMs";
export const MIN_TEST_IDLE_MS = 10_000;
export function idleMsFor(appEnv: string | undefined, stored: string | null): number {
  if (appEnv !== "staging" && appEnv !== "local") return USER_IDLE_MS;
  const ms = Number(stored);
  return stored && Number.isInteger(ms) && ms >= MIN_TEST_IDLE_MS && ms <= USER_IDLE_MS ? ms : USER_IDLE_MS;
}

/** The notice's and the pill's words, in one place. */
export const IDLE_TEXT = {
  title: "Live updates paused",
  body: "No activity for 30 minutes, so this page stopped refreshing. What you see may be out of date.",
  lastUpdated: (time: string) => `Last updated at ${time}`,
  button: "Resume updates",
  pill: (time: string) => (time ? `Updates paused · ${time}` : "Updates paused"),
  pillHover: "No activity for 30 minutes: this page stopped refreshing. Click, or move the mouse, to resume.",
  updating: "Updating…",
} as const;
